import { clsx } from "clsx";
import { Grid } from "@/components/Grid/Grid";
import { KitFooter } from "@/components/kit/KitFooter/KitFooter";
import { KitNavbar } from "@/components/kit/KitNavbar/KitNavbar";
import { renderEmphasis } from "@/components/kit/lib/emphasis";
import "@/components/guide/guide-system.css";
import "./blog.css";
import type { BlogIndexView } from "@/utils/blog/types";
import { BlogCtaBand } from "./BlogCta";
import { BlogPostCard } from "./BlogPostCard";

export interface BlogIndexPageProps {
  view: BlogIndexView;
}

export function BlogIndexPage({ view }: BlogIndexPageProps) {
  return (
    <div className="guide-page min-h-screen" data-component="BlogIndexPage">
      <KitNavbar ctaLabel="Консультация" ctaHref="/coaching" />

      <header className="blog-hero" data-section="blog-hero">
        <div className="wrap">
          <p className="eyebrow">{view.eyebrow}</p>
          <h1 className="blog-hero-title">{renderEmphasis(view.title, view.titleAccent)}</h1>
          <p className="blog-hero-sub">{view.subtitle}</p>
          <nav className="blog-chips" aria-label="Темы блога">
            {view.tags.map((tag) => (
              <a
                key={tag.href}
                href={tag.href}
                className={clsx("blog-chip", tag.active && "blog-chip-active")}
                data-track={`blog-index.tag.${tag.label}`}
              >
                {tag.label}
              </a>
            ))}
          </nav>
        </div>
      </header>

      <main className="blog-main">
        <div className="wrap">
          {view.featured ? (
            <a
              href={view.featured.href}
              className="blog-featured"
              data-section="blog-featured"
              data-track="blog-index.card.featured"
            >
              <div
                className={clsx(
                  "blog-featured-cover",
                  !view.featured.cover && "blog-card-cover-empty",
                )}
              >
                {view.featured.cover ? (
                  <img src={view.featured.cover} alt="" loading="lazy" />
                ) : null}
              </div>
              <div className="blog-featured-body">
                <div className="blog-card-meta">
                  {view.featured.tagLabel ? (
                    <span className="pill-tag lav">{view.featured.tagLabel}</span>
                  ) : null}
                  <span className="blog-card-dot">
                    {view.featured.dateLabel}
                    {view.featured.dateLabel && view.featured.readLabel ? " · " : ""}
                    {view.featured.readLabel}
                  </span>
                </div>
                <h2 className="blog-featured-title">{view.featured.title}</h2>
                {view.featured.description ? (
                  <p className="blog-featured-desc">{view.featured.description}</p>
                ) : null}
                <span className="blog-read-link">Читать статью →</span>
              </div>
            </a>
          ) : null}

          {view.posts.length > 0 ? (
            <Grid
              columns={{ base: 1, md: 2, lg: 3 }}
              gap={{ base: 32, lg: 40 }}
              className="blog-grid"
              data-section="blog-grid"
            >
              {view.posts.map((post, index) => (
                <BlogPostCard key={post.slug} post={post} index={index} />
              ))}
            </Grid>
          ) : (
            <p className="blog-empty">Скоро здесь появятся статьи.</p>
          )}

          {view.showMoreHref ? (
            <div className="blog-more">
              <a
                href={view.showMoreHref}
                className="ds-btn ds-btn-outline"
                data-track="blog-index.cta.show_more"
              >
                Показать ещё
              </a>
            </div>
          ) : null}
        </div>
      </main>

      <BlogCtaBand href={view.leadMagnetHref} />
      <KitFooter />
    </div>
  );
}

BlogIndexPage.displayName = "BlogIndexPage";
